import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { broker } from '#~/agent/broker.js';
import { useAgents } from '#~/agent/hooks.js';
import { shallow } from 'zustand/shallow';
import {
  fetchRequestLink,
  linkAgent,
  unlinkAgent,
  directLinkStatusAndRequestAgent,
  type LinkRequest
} from '#~/api/agent/index.js';
import type { AgentSystemInfo } from './screens/device-img.js';
import { useAuth0 } from '#~/auth/use-auth0.js';
import { ElectronAPI } from '#~/electron/api.js';
// ENG-4154: read the IdentityState store, not a per-consumer bridge poll.
import { resolveLinkedAgentId, useIdentityState } from '#~/electron/identity-store.js';
import {
  getDesktopUpdateCheckFailed,
  subscribeDesktopUpdateCheckFailed
} from '#~/electron/update-check-status.js';
import { isElectronApp } from '#~/electron/utils.js';
import { useRoom, useUserRole, type UserRole } from '#~/room/hooks.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { trackIntercomEvent, INTERCOM_EVENTS } from '#~/intercom/index.js';
import { getCurrentVendor } from '@studio/design-system/themes/vendor.js';
import { usePrefState } from '#~/preferences/hooks.js';
import { usePrompt } from '@studio/design-system/prompt';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import type { Agent } from '@studio/models/agent';
import { useMutation, useQuery } from '@tanstack/react-query';
import { StatusUpdateState } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { T_Maybe } from '@tryiris-ai/js-types';
import * as Commands from '#~/agent/commands/index.js';
import { useWiring } from '../monitoring/wiring.js';
import { toHexFromId } from '#~/api/utils.js';
import type { DiscoveredService } from '#~/agent/discovery/utils.js';
import { useMultiProxyStateQuery } from '#~/statequery/hook.js';
import type { State } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { Discovery } from '#~/device/discovery/index.js';
import type { User } from '@studio/models/user';
import { queryKeys } from '#~/react-query/index.js';
import { prepareDirectLinkService } from './direct-link-service.js';
import { LINK_REQUEST_EXPIRED_MESSAGE } from '#~/api/agent/link-request-expiry.js';

let __agentLinkDetails__:
  | {
      agentId: Agent['id'];
      requestId: LinkRequest['id'];
    }
  | undefined;

/**
 * Ceiling for a single direct add-camera link leg. A hang still needs a
 * presentation deadline so Authenticate Source is not an infinite spinner.
 * Per rule: reference-rules-in-comments (ai.knowledge/global).
 */
export const DIRECT_LINK_TIMEOUT_MS = 30_000;

/** Raised when a link leg exceeds its client-side presentation deadline. */
export class DirectLinkTimeoutError extends Error {
  readonly timeoutMs: number;
  readonly leg: string;

  constructor(leg: string, timeoutMs: number) {
    super(`Timed out after ${timeoutMs}ms waiting for ${leg}`);
    this.name = 'DirectLinkTimeoutError';
    this.timeoutMs = timeoutMs;
    this.leg = leg;
  }
}

export type DirectLinkTimeoutOptions = {
  timeoutMs?: number;
  /**
   * Display-only deadline. When set, the timer never rejects `work` and never
   * settles the mutation — Authenticate Source can show R8 while the raw
   * details/token/cloud promise stays the in-flight owner. Callers that omit
   * this (search-devices) keep the historical reject-on-timeout behavior.
   */
  onTimeout?: (error: DirectLinkTimeoutError) => void;
};

/**
 * Bound `work` by `timeoutMs`. Without `onTimeout`, the race rejects so
 * search-devices still recovers from a hang. With `onTimeout`, the deadline
 * is display-only: the timer fires the callback and `work` remains pending
 * until it actually settles.
 */
export const withDirectLinkTimeout = <T>(
  work: Promise<T>,
  leg: string,
  timeoutMs: number = DIRECT_LINK_TIMEOUT_MS,
  onTimeout?: (error: DirectLinkTimeoutError) => void
): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    let finished = false;
    const timer = setTimeout(() => {
      if (finished) return;
      const timeoutError = new DirectLinkTimeoutError(leg, timeoutMs);
      if (onTimeout) {
        onTimeout(timeoutError);
        return;
      }
      finished = true;
      reject(timeoutError);
    }, timeoutMs);
    work.then(
      (value) => {
        if (finished) return;
        finished = true;
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        if (finished) return;
        finished = true;
        clearTimeout(timer);
        reject(error);
      }
    );
  });

/**
 * `getLinkAgentIds`.
 *
 * @description A function that returns the agent ID and request ID for the currently linked agent.
 *
 * FC-1.5 flow split: `flow` selects the Electron-main IPC channel. `'auto'`
 * (default — preserves every existing call site) rides `bridge:linkv2`, the
 * Auto Link controller's channel, which main DENIES for Hive as bypass
 * protection (FC-3.5). `'manual'` rides `bridge:link-manual`, the manual link
 * screen's user-driven channel, allowed for BOTH vendors (FC-1.2: Hive links
 * manually). Without this split the Hive "This Computer" card polled a denied
 * channel forever (observed 2026-08-07: 11 denials/session, card never
 * rendered, local machine unlinkable).
 * Per rule: reference-rules-in-comments (ai.knowledge/global). See
 * docs/hive-shared-release/functional-contract.md FC-1.
 *
 * @returns The agent ID and request ID for the currently linked agent.
 */
export async function getLinkAgentIds(
  force = false,
  flow: 'auto' | 'manual' = 'auto',
  deadlineAt?: number
) {
  if (__agentLinkDetails__ && !force) {
    return __agentLinkDetails__;
  }

  // Direct await (no Promise.any race) so rejections propagate as-is.
  const linkPromise =
    flow === 'manual'
      ? ElectronAPI.sendLinkRequestManual()
      : deadlineAt === undefined
        ? ElectronAPI.sendLinkRequestV2()
        : ElectronAPI.sendLinkRequestV2(deadlineAt);

  let link: T_Maybe<Agent['id']>;

  try {
    link = await linkPromise;
  } catch (error) {
    console.error('Error while linking agent', error);

    throw error;
  }

  if (!link) {
    throw new Error('Linking agent failed');
  }

  // Extract the path from the URL
  const path = new URL(link).pathname;

  // Apply regex to the path
  const regex = /^\/agent\/([^/]+)\/link\/(.*)$/;
  const match = path.match(regex);

  if (!match) {
    return { agentId: '' as Agent['id'], requestId: '' as LinkRequest['id'] };
  }
  const [, agentId, requestId] = match as [string, Agent['id'], LinkRequest['id']];
  __agentLinkDetails__ = { agentId, requestId };

  return { agentId, requestId };
}

/**
 * `useLinkAgentDetails` custom hook.
 *
 * @description A hook that fetches the agent details for the currently linked agent.
 */
export const useLinkAgentDetails = () => {
  const { getAccessTokenSilently } = useAuth0();

  const {
    data: agent,
    refetch: reload,
    error,
    isLoading: loading
  } = useQuery({
    queryKey: ['agentDetails'],
    queryFn: async () => {
      // Manual flow (FC-1.5): this hook feeds the manual link screen's "This
      // Computer" card — it must ride the vendor-agnostic manual channel, not
      // the Iris-only auto-link channel (see getLinkAgentIds).
      const { agentId, requestId } = await getLinkAgentIds(false, 'manual');

      const token = await getAccessTokenSilently();

      return fetchRequestLink(requestId, token).then((v) => ({
        ...v,
        id: agentId
      }));
    },
    retry: 10
  });

  return { agent, reload, error, loading };
};

/**
 * `useGetMachineAgentId` custom hook.
 *
 * @description A hook that returns the linked machine agent ID and resolution state.
 */
export const useGetMachineAgentId = () => {
  const identity = useIdentityState();
  const isElectron = isElectronApp();
  const { isLoading, isError, data } = useQuery({
    queryKey: queryKeys.machineAgentId(),
    // TanStack Query v5 treats a queryFn that RESOLVES `undefined` as a query
    // FAILURE ("Query data cannot be undefined") — silent in prod builds. On an
    // UNLINKED machine `resolveLinkedAgentId()` legitimately resolves undefined,
    // which latched `isError=true` → resolution 'error' forever → the room
    // route's empty-studio grace never armed → infinite "Loading your studio…"
    // (hive-enablement boot hang, 2026-08-07 QA). Encode "authoritatively not
    // linked" as `null` so v5 sees settled data, and map back to undefined at
    // the return boundary so consumers keep the old contract.
    // Per rule: absent-data-gates-permissively + reference-rules-in-comments
    // (ai.knowledge/global).
    queryFn: async (): Promise<Agent['id'] | null> => {
      if (!isElectron) {
        return null;
      }
      return (await resolveLinkedAgentId()) ?? null;
    }
  });

  // Per `absent-data-gates-permissively` (ai.knowledge/global), keep loading
  // and error distinct from an authoritative loaded-without-identity result.
  const resolution: 'unknown' | 'loaded' | 'error' =
    isLoading || (isElectron && identity.status === 'resolving')
      ? 'unknown'
      : isError || (isElectron && identity.status === 'error')
        ? 'error'
        : 'loaded';

  return { loading: isLoading, hasError: isError, resolution, agentId: data ?? undefined };
};

/**
 * `useUnlinkAgent` custom hook.
 *
 * @description A hook that unlinks the agent from the room.
 */
export const useUnlinkAgent = () => {
  const { getAccessTokenSilently } = useAuth0();

  const roomId = useRoomId();
  const { track } = useAnalytics();

  const {
    isPending,
    mutateAsync: unlinkBridge,
    error
  } = useMutation({
    mutationFn: async (agentId: Agent['id']) => {
      return new Promise<void>(async (resolve, reject) => {
        let cloudUnlinkPromise: Promise<void>;
        let directUnlinkPromise: Promise<void>;

        try {
          cloudUnlinkPromise = unlinkAgent(agentId, await getAccessTokenSilently());
          directUnlinkPromise = Commands.unlinkDirect(agentId);
        } catch (err) {
          reject(new Error(`Error while unlinking agent ${err}`));
          return;
        }

        try {
          // Unlink the agent from the cloud.
          await cloudUnlinkPromise;

          resolve();

          await directUnlinkPromise;
        } catch (err) {
          reject(new Error(`Error while unlinking agent ${err}`));
        } finally {
          // Track the bridge unlinking event
          track(EVENT_NAMES.USER_UNLINK_BRIDGE, EVENT_CATEGORIES.BRIDGE, {
            bridgeType: 'local',
            studioId: roomId,
            agentId
          });
        }
      });
    }
  });

  return { loading: isPending, error, unlinkBridge };
};

/**
 * `useLinkAgent` custom hook.
 *
 * @description A hook that links the agent to the room.
 */
export const useLinkAgent = (onSuccess: (linked: boolean) => void) => {
  const roomId = useRoomId();

  const { getAccessTokenSilently, user: auth0User } = useAuth0();
  const { track } = useAnalytics();

  const pendingPreferenceValue = useRef<string | undefined>(undefined);

  const userScopedPreferenceKey = useMemo(
    () => (auth0User?.sub ? `${auth0User.sub}:intercom.first-bridge-linked` : undefined),
    [auth0User?.sub]
  );

  // Track if this is the first time a user has linked a bridge
  const [storedHasLinkedBridge, setStoredHasLinkedBridge] = usePrefState<string>(
    userScopedPreferenceKey ?? 'intercom.first-bridge-linked',
    'false'
  );

  const hasLinkedBridge = userScopedPreferenceKey
    ? storedHasLinkedBridge
    : (pendingPreferenceValue.current ?? 'false');

  const setHasLinkedBridge = useCallback(
    (value: string) => {
      if (!userScopedPreferenceKey) {
        pendingPreferenceValue.current = value;
        return;
      }

      setStoredHasLinkedBridge(value);
    },
    [userScopedPreferenceKey, setStoredHasLinkedBridge]
  );

  useEffect(() => {
    if (!userScopedPreferenceKey || pendingPreferenceValue.current === undefined) {
      return;
    }

    setStoredHasLinkedBridge(pendingPreferenceValue.current);
    pendingPreferenceValue.current = undefined;
  }, [userScopedPreferenceKey, setStoredHasLinkedBridge]);

  const {
    isPending,
    mutate: linkBridge,
    error
  } = useMutation({
    mutationFn: async () => {
      return new Promise<boolean>(async (resolve, reject) => {
        try {
          // Manual flow (FC-1.5): this mutation IS the manual link screen's
          // "Link Bridge" action — vendor-agnostic manual channel, never the
          // Iris-only auto-link channel (see getLinkAgentIds).
          const { agentId, requestId } = await getLinkAgentIds(true, 'manual');
          const token = await getAccessTokenSilently();

          await linkAgent(agentId, requestId, roomId, token);

          // Track the bridge linking event in analytics
          track(EVENT_NAMES.USER_LINK_BRIDGE, EVENT_CATEGORIES.BRIDGE, {
            bridgeType: 'local',
            studioId: roomId,
            agentId
          });

          // Track first-time bridge linking in Intercom (for onboarding flows)
          if (isElectronApp() && hasLinkedBridge !== 'true') {
            trackIntercomEvent(INTERCOM_EVENTS.FIRST_TIME_ELECTRON_ACCESS, {
              vendor: getCurrentVendor(),
              roomId: roomId.toString(),
              agentId: agentId.toString()
            });
            setHasLinkedBridge('true');
          }

          resolve(true);
        } catch (err) {
          track(EVENT_NAMES.USER_LINK_BRIDGE, EVENT_CATEGORIES.BRIDGE, {
            bridgeType: 'local',
            successfulLink: false,
            studioId: roomId
            // TODO: figure out how to add agentId here later...
          });
          // The desktop manual flow creates a fresh request on its next click.
          // Preserve expiry so its UI can explain why this attempt ended.
          reject(
            new Error(
              err instanceof Error && 'code' in err && err.code === 412
                ? LINK_REQUEST_EXPIRED_MESSAGE
                : 'Error while linking agent'
            )
          );
        }
      });
    },
    onSuccess
  });

  return { loading: isPending, error, linkBridge };
};

// Preserve the failing operation through React Query without changing the original
// error delivered to shared callers. The token read and cloud write have different meanings.
class DirectLinkOperationError extends Error {
  constructor(
    readonly original: Error,
    readonly operation: 'auth-access-token' | 'cloud-link-agent'
  ) {
    super(original?.message ?? 'Direct link operation failed');
  }
}

/**
 * `useDirectLinkAgent` custom hook.
 *
 * @description A hook that directly links the agent to the room.
 */
export const useDirectLinkAgent = (
  onSuccess: (linked: boolean) => void,
  onError: (error: Error, operation?: 'auth-access-token' | 'cloud-link-agent') => void,
  // timeoutMs is injectable so tests can drive the hang→timeout path on a small
  // budget; production callers omit it and get DIRECT_LINK_TIMEOUT_MS.
  options?: DirectLinkTimeoutOptions
) => {
  const roomId = useRoomId();
  const { getAccessTokenSilently } = useAuth0();
  const timeoutMs = options?.timeoutMs ?? DIRECT_LINK_TIMEOUT_MS;
  const onTimeoutRef = useRef(options?.onTimeout);
  onTimeoutRef.current = options?.onTimeout;

  const {
    isPending,
    mutate: linkBridgeDirect,
    error
  } = useMutation({
    mutationFn: async ({
      agentId,
      requestId,
      // @ts-expect-error
      token,
      shouldContinue
    }: {
      agentId: Agent['id'];
      requestId: LinkRequest['id'];
      token?: string;
      /** Rechecked after the token read so dismiss/timeout/identity cannot write. */
      shouldContinue?: () => boolean;
    }) => {
      // Bound the WHOLE leg — token read and link call. With onTimeout the
      // deadline is display-only; the mutation stays pending until work
      // settles so a display timeout cannot be retried blindly.
      let tokenAcquired = false;
      try {
        await withDirectLinkTimeout(
          (async () => {
            const accessToken = await getAccessTokenSilently();
            // Electron Auth.getAccessToken can resolve null; no usable token means
            // no cloud write, and does not by itself prove a sign-in diagnosis.
            if (typeof accessToken !== 'string' || !accessToken) {
              throw new Error('No access token available');
            }
            tokenAcquired = true;
            if (shouldContinue && !shouldContinue()) {
              throw new Error('Attempt superseded');
            }
            await linkAgent(agentId, requestId, roomId, accessToken);
          })(),
          'cloud link to agent',
          timeoutMs,
          onTimeoutRef.current
            ? (error) => {
                onTimeoutRef.current?.(
                  tokenAcquired
                    ? error
                    : new DirectLinkTimeoutError('access token', error.timeoutMs)
                );
              }
            : undefined
        );
      } catch (error) {
        throw new DirectLinkOperationError(
          error as Error,
          tokenAcquired ? 'cloud-link-agent' : 'auth-access-token'
        );
      }
      return true;
    },
    onSuccess,
    onError: (failure: DirectLinkOperationError) => onError(failure.original, failure.operation)
  });

  return { isDirectLinkLoading: isPending, error: error?.original ?? null, linkBridgeDirect };
};

/**
 * @internal `useRoomId` custom hook.
 *
 * @description A hook that returns the ID of the current room.
 *
 * @returns The ID of the current room.
 */
const useRoomId = () => {
  const roomId = useRoom(({ id }) => id);
  return roomId;
};

/**
 * `useRoomAgent` custom hook.
 *
 * @description A hook that returns the agent for the current room.
 */
export const useRoomAgent = (_agentId?: Agent['id']) => {
  const prompt = usePrompt();
  const agentIds = useAgents((µ) => µ.map((a) => a.id), shallow);
  const roomId = useRoomId();
  const wiring = useWiring(agentIds, roomId);

  const [agent, agentId] = useMemo(() => {
    let foundAgent: Agent | undefined;
    if (_agentId) {
      foundAgent = Object.values(wiring.agents).find((a) => a.id === _agentId);
    }
    if (!foundAgent) {
      foundAgent = Object.values(wiring.agents).find((a) => !a.embedded);
    }
    return [foundAgent, foundAgent?.id];
  }, [_agentId, wiring.agents]);

  // Compute all derived values regardless of agent availability
  const offline = agent?.status === 'offline';
  const agentStats = agentId ? wiring.summary.agentStats?.[agentId] : undefined;
  const proxy = agentId ? broker.acquire(agentId) : undefined;
  const stateUpdateStatus = proxy?.state?.status?.update;
  const checkFailed = useSyncExternalStore(
    subscribeDesktopUpdateCheckFailed,
    getDesktopUpdateCheckFailed,
    () => false
  );

  const updateStatus = useMemo(
    () => ({
      updateAvailable: [
        StatusUpdateState.UPDATE_STATE_UPDATE_AVAILABLE,
        StatusUpdateState.UPDATE_STATE_DOWNLOADING,
        StatusUpdateState.UPDATE_STATE_WAITING_TO_INSTALL
      ].includes(stateUpdateStatus?.state ?? StatusUpdateState.UPDATE_STATE_IDLE),
      isDownloading: stateUpdateStatus?.state === StatusUpdateState.UPDATE_STATE_DOWNLOADING,
      isReadyToInstall:
        stateUpdateStatus?.state === StatusUpdateState.UPDATE_STATE_WAITING_TO_INSTALL,
      isInstalling: stateUpdateStatus?.state === StatusUpdateState.UPDATE_STATE_INSTALLING,
      bytesDownloaded: stateUpdateStatus?.bytesDownloaded ?? 0,
      bytesTotal: stateUpdateStatus?.bytesTotal ?? 0,
      bytesPerSecond: stateUpdateStatus?.bytesPerSecond ?? 0,
      checkFailed
    }),
    [stateUpdateStatus, checkFailed]
  );

  // Create stable callback functions using useCallback
  const update = useCallback(() => {
    if (!agentId) return;

    prompt.confirm({
      title: `Update Bridge?`,
      message: `Are you sure you want to update the ${vendorName()} Agent software?  This will disconnect all video and audio sources until the agent is updated and restarted.`,
      okay: {
        label: 'Update Bridge',
        variant: 'danger'
      },
      onConfirm: (answer: boolean) => {
        if (!answer) {
          return;
        }

        wiring.handleUpdate(agentId);
      }
    });
  }, [agentId, prompt, wiring]);

  const unlink = useCallback(() => {
    if (!agentId) return;

    wiring.handleUnlink(agentId, () => {
      if (isElectronApp()) {
        ElectronAPI.onReloadStudio();
      }
      window.location.reload();
    });
  }, [agentId, wiring]);

  const restart = useCallback(() => {
    if (!agentId) return;

    wiring.handleRestart(agentId);
  }, [agentId, wiring]);

  // Only return early AFTER all hooks have been called
  if (!agentId || !agent) {
    return undefined;
  }

  return {
    agent,
    wiring,
    agentStats,
    offline,
    updateStatus,
    update,
    unlink,
    restart
  };
};

/**
 * `usePermissions` custom hook.
 *
 * @description A hook that returns the permissions for the current user.
 */
export const usePermissions = () => {
  const userRole = useUserRole();
  const isAdminOrOwnerOrProducer = (
    ['admin', 'owner', 'producer'] satisfies UserRole[] as string[]
  ).includes(userRole);
  const isAdminOrOwner = (['admin', 'owner'] satisfies UserRole[] as string[]).includes(userRole);

  return {
    canSwitchBridge: isAdminOrOwner,
    canUnlink: isAdminOrOwner,
    canAccessBridge: isAdminOrOwnerOrProducer
  };
};

/**
 * `getAgentSystemInfo` custom hook.
 *
 * @description A hook that returns the operating system and machine type of all agents.
 */
export const useAgentsSystemInfo = (
  agents: { id: Agent['id']; name: Agent['name']; status: Agent['status'] }[]
) => {
  // 1) fetch all agents operating systems:
  const agentsOperatingSystemAndMachineType = useMultiProxyStateQuery((state: T_Maybe<State>) => {
    return {
      os: state?.status?.encoder?.os,
      machineType: state?.status?.encoder?.machineType
    };
  });

  const systemInfo: Record<Agent['id'], AgentSystemInfo> = {};

  // 2) map the operating systems to the agents
  agents.forEach((agent) => {
    const agentData = agentsOperatingSystemAndMachineType[agent.id];
    if (!agentData) return;

    systemInfo[agent.id] = {
      // @ts-expect-error
      os: agentData.os,
      // @ts-expect-error
      machineType: agentData.machineType
    };
  });

  return {
    agentsOperatingSystemAndMachineType,
    systemInfo
  };
};

/**
 * `useConnectToMDNSService` custom hook.
 *
 * @description A hook that provides functionality to connect to an mDNS discovered service.
 */
export const useConnectToMDNSService = (options?: DirectLinkTimeoutOptions) => {
  // timeoutMs injectable for tests; production callers use DIRECT_LINK_TIMEOUT_MS.
  const timeoutMs = options?.timeoutMs ?? DIRECT_LINK_TIMEOUT_MS;
  const onTimeoutRef = useRef(options?.onTimeout);
  onTimeoutRef.current = options?.onTimeout;
  const {
    mutateAsync: requestDirectLinkToAgentDetails,
    isPending: isRequesting,
    error
  } = useMutation({
    mutationFn: async ({
      service,
      bridgeLinkingRequestId,
      username,
      password,
      user,
      studioName,
      isPreApprovedForUnlink
    }: {
      service: DiscoveredService | T_Maybe<Discovery>;
      bridgeLinkingRequestId?: string;
      username?: string;
      password?: string;
      user?: Pick<User, 'email' | 'firstName' | 'lastName' | 'id'>;
      studioName?: string;
      isPreApprovedForUnlink?: boolean;
    }) => {
      return new Promise(async (resolve, reject) => {
        try {
          const serviceToUse = prepareDirectLinkService(service!);

          // Read link data through Electron. With onTimeout the deadline only
          // releases presentation; raw work stays pending to prevent duplicate writes.
          // Shared callers without onTimeout use the rejecting deadline instead.
          const { agentDirectStatus, agentDirectLinkDetails } = await withDirectLinkTimeout(
            directLinkStatusAndRequestAgent({
              service: serviceToUse,
              ...(bridgeLinkingRequestId && { bridgeLinkingRequestId }),
              ...(username && { username }),
              ...(password && { password }),
              ...(user && { user }),
              ...(studioName && { studioName }),
              ...(isPreApprovedForUnlink && { isPreApprovedForUnlink })
            }),
            'link details request',
            timeoutMs,
            onTimeoutRef.current
          );

          if (!agentDirectStatus && !agentDirectLinkDetails) {
            throw new Error('Failed to retrieve agent link details');
          }

          // Preserve status.error. Missing status must not be rewritten as
          // linked:false — Authenticate Source refuses that as a service failure.
          resolve({
            statusDetails: agentDirectStatus
              ? {
                  linked: agentDirectStatus.linked,
                  bridgeId: toHexFromId(agentDirectStatus.bridgeId),
                  error: agentDirectStatus.error
                }
              : undefined,
            linkDetails: agentDirectLinkDetails
              ? {
                  requestId: toHexFromId(agentDirectLinkDetails.requestId),
                  bridgeId: toHexFromId(agentDirectLinkDetails.bridgeId),
                  token: agentDirectLinkDetails.token,
                  lockoutStatus: agentDirectLinkDetails.deviceLoginLockoutStatus
                    ? {
                        isLocked: agentDirectLinkDetails.deviceLoginLockoutStatus?.locked,
                        unlockTimeSeconds:
                          agentDirectLinkDetails.deviceLoginLockoutStatus?.unlockTimeSeconds
                      }
                    : undefined,
                  error: agentDirectLinkDetails.error
                }
              : undefined
          });
        } catch (err) {
          console.error('___Error connecting to agent:', err);
          reject(err);
        }
      });
    }
  });

  return {
    requestDirectLinkToAgentDetails,
    isRequesting,
    error
  };
};
